import { useState } from "react";
import type { OperationType } from "@boosted/shared/bajie";
import { useDeviceOperation, useRefreshById, type CabinetRow } from "../lib/api.js";
import { ConfirmModal } from "./ConfirmModal.js";

interface ActionDef {
  label: string;
  op: OperationType;
  variant: "primary" | "secondary" | "danger";
  hint: string;
  hardConfirm?: boolean;
}

const ACTIONS: ActionDef[] = [
  { label: "Restart", op: "restart", variant: "primary", hint: "Soft reboot the kiosk controller. First thing to try when something's misbehaving." },
  { label: "Heartbeat", op: "heartbeat", variant: "secondary", hint: "Ping the kiosk to confirm it's alive." },
  { label: "Report", op: "report", variant: "secondary", hint: "Nudge the kiosk to push a fresh CABINET_STATUS." },
  {
    label: "Pop all",
    op: "popall",
    variant: "danger",
    hint: "Eject every battery from this kiosk. Service-only — for popping a single battery, click a slot in the grid below.",
    hardConfirm: true,
  },
];

const variantClass: Record<ActionDef["variant"], string> = {
  primary:
    "bg-gradient-to-br from-brand-orange to-brand-orangeDark text-white shadow-[0_4px_14px_-4px_rgba(249,115,22,0.6)] ring-1 ring-brand-orange/40 hover:brightness-110",
  secondary:
    "bg-zinc-800/80 text-zinc-200 ring-1 ring-zinc-700 hover:bg-zinc-700 hover:ring-brand-purple/40",
  danger: "bg-rose-500/15 text-rose-200 ring-1 ring-rose-500/30 hover:bg-rose-500/25",
};

export function CabinetActions({ cabinet }: { cabinet: CabinetRow }) {
  const op = useDeviceOperation();
  const refresh = useRefreshById();
  const [confirming, setConfirming] = useState<ActionDef | null>(null);
  const [lastResult, setLastResult] = useState<{ op: OperationType; ok: boolean; msg?: string } | null>(null);

  function run(action: ActionDef) {
    if (action.hardConfirm) {
      setConfirming(action);
      return;
    }
    fire(action);
  }

  function fire(action: ActionDef) {
    setConfirming(null);
    op.mutate(
      {
        cabinetId: cabinet.id,
        operationType: action.op,
        slotNum: 0,
        reason: "ops",
      },
      {
        onSuccess: () => setLastResult({ op: action.op, ok: true }),
        onError: (err) =>
          setLastResult({ op: action.op, ok: false, msg: (err as Error).message }),
      },
    );
  }

  return (
    <div className="card p-5">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-zinc-200">Cabinet actions</h2>
        <button
          onClick={() => refresh.mutate(cabinet.id)}
          disabled={refresh.isPending}
          className="btn-purple"
        >
          {refresh.isPending ? "Refreshing…" : "Refresh from Bajie"}
        </button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {ACTIONS.map((a) => (
          <div key={a.op} className="rounded-lg border border-zinc-800/60 bg-zinc-950/40 p-3">
            <button
              onClick={() => run(a)}
              disabled={op.isPending}
              className={
                "w-full rounded-md px-3 py-2 text-xs font-medium transition disabled:opacity-50 " +
                variantClass[a.variant]
              }
            >
              {a.label}
              {a.hardConfirm && <span className="ml-1.5 text-[10px] opacity-80">⚠</span>}
            </button>
            <p className="mt-2 text-[11px] leading-relaxed text-zinc-500">{a.hint}</p>
          </div>
        ))}
      </div>

      {lastResult && (
        <div
          className={
            "mt-4 rounded-md px-3 py-2 text-xs " +
            (lastResult.ok
              ? "bg-emerald-500/10 text-emerald-200 ring-1 ring-emerald-500/30"
              : "bg-rose-500/10 text-rose-200 ring-1 ring-rose-500/30")
          }
        >
          {lastResult.ok ? `✓ ${lastResult.op} sent to ${cabinet.id}` : `✗ ${lastResult.op}: ${lastResult.msg}`}
        </div>
      )}
      <div className="mt-3 text-[11px] text-zinc-500">
        Slot-level Pop / Lock / Unlock / Eject-for-repair are below in the slot grid.
      </div>

      <ConfirmModal
        open={confirming !== null}
        title={confirming ? `${confirming.label} on ${cabinet.id}?` : ""}
        body={
          confirming ? (
            <>
              <p>
                You&apos;re about to <strong className="text-rose-300">{confirming.label.toLowerCase()}</strong>{" "}
                on cabinet <span className="font-mono text-zinc-100">{cabinet.id}</span>. Every
                battery currently in the kiosk will be ejected to the
                customer-facing slots — they will not be inserted back automatically.
              </p>
              <p className="mt-3 text-zinc-400">
                Type the cabinet SN below to confirm. Are you flippin&apos; sure, man?
              </p>
            </>
          ) : null
        }
        challenge={cabinet.id}
        challengeLabel={`Type the cabinet SN (${cabinet.id}) to arm the button`}
        confirmLabel={confirming ? confirming.label : "Confirm"}
        busy={op.isPending}
        onCancel={() => setConfirming(null)}
        onConfirm={() => confirming && fire(confirming)}
      />
    </div>
  );
}
